import React from 'react';
import Cell from './Cell';
import { GridState, Idol, Position } from 'types';
import { GRID_ROWS, GRID_COLS, CELL_SIZE } from 'constants/index';
import { canPlaceItem, safeGetItemSizeProperty } from 'utils';
import Tooltip from 'components/UI/Tooltip';

interface GridProps {
  grid: GridState;
  placedItems: Idol[];
  draggedItem: Idol | null;
  hoverPosition: Position | null;
  tooltipItem: Idol | null;
  mousePosition: { x: number; y: number };
  onDragOver: (e: React.DragEvent<HTMLDivElement>, row: number, col: number) => void;
  onDragEnter: (e: React.DragEvent<HTMLDivElement>, row: number, col: number) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, row: number, col: number) => void;
  onItemClick: (itemId: string) => void;
  onMouseEnter: (item: Idol) => void;
  onMouseLeave: () => void;
  gridSizeMultiplier?: number;
  onClearGrid: () => void;
}

const Grid: React.FC<GridProps> = ({
  grid,
  placedItems,
  draggedItem,
  hoverPosition,
  tooltipItem,
  mousePosition,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onItemClick,
  onMouseEnter,
  onMouseLeave,
  gridSizeMultiplier = 1,
  onClearGrid,
}) => {
  const adjustedCellSize = CELL_SIZE * gridSizeMultiplier;
  
  return (
    <div className="flex flex-col items-center">
      <div className="bg-gray-700 text-gray-300 text-xs px-3 py-1.5 rounded-md mb-2 text-center shadow-sm">
        <span>Drag items to place • Click to remove • Hover for tooltip</span>
      </div>
      
      <div 
        className="bg-gray-800 rounded-lg border-2 border-gray-700 overflow-hidden grid-container"
        style={{
          width: `${GRID_COLS * adjustedCellSize}px`,
          height: `${GRID_ROWS * adjustedCellSize}px`,
        }}
      >
        <div 
          className="relative"
          style={{ 
            display: 'grid',
            gridTemplateRows: `repeat(${GRID_ROWS}, ${adjustedCellSize}px)`,
            gridTemplateColumns: `repeat(${GRID_COLS}, ${adjustedCellSize}px)`,
            width: '100%',
            height: '100%',
            background: '#111827',
          }}
        >
          {grid.map((row, rowIdx) => (
            row.map((cell, colIdx) => {
              const isHighlighted = Boolean(
                hoverPosition && 
                draggedItem && 
                rowIdx >= hoverPosition.row && 
                rowIdx < hoverPosition.row + safeGetItemSizeProperty(draggedItem, 'height', 1) && 
                colIdx >= hoverPosition.col && 
                colIdx < hoverPosition.col + safeGetItemSizeProperty(draggedItem, 'width', 1)
              );
              
              const isValidPlacement = Boolean(
                hoverPosition && 
                draggedItem && 
                canPlaceItem(grid, hoverPosition.row, hoverPosition.col, draggedItem)
              );
              
              return (
                <Cell
                  key={`${rowIdx}-${colIdx}`}
                  cellState={cell}
                  position={{ row: rowIdx, col: colIdx }}
                  size={adjustedCellSize}
                  _draggedItem={draggedItem}
                  _hoverPosition={hoverPosition}
                  isHighlighted={isHighlighted}
                  isValidPlacement={isValidPlacement}
                  onDragOver={onDragOver}
                  onDragEnter={onDragEnter}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                />
              );
            })
          ))}
          
          {Array.isArray(placedItems) && placedItems.map(item => {
            if (!item || !item.position) return null;
            
            const { row, col } = item.position;
            const itemWidth = safeGetItemSizeProperty(item, 'width', 1);
            const itemHeight = safeGetItemSizeProperty(item, 'height', 1);
            const itemColor = safeGetItemSizeProperty(item, 'color', '#888888');
            
            return (
              <div
                key={item.id}
                className="absolute border border-gray-600 flex items-center justify-center cursor-pointer hover:brightness-110 transition-all duration-200"
                style={{
                  backgroundColor: itemColor,
                  width: `${itemWidth * adjustedCellSize}px`,
                  height: `${itemHeight * adjustedCellSize}px`,
                  top: `${row * adjustedCellSize}px`,
                  left: `${col * adjustedCellSize}px`,
                  zIndex: 10
                }}
                onClick={() => onItemClick(item.id)}
                onMouseEnter={() => onMouseEnter(item)}
                onMouseLeave={onMouseLeave}
              />
            );
          })}
          
          {tooltipItem && <Tooltip item={tooltipItem} position={mousePosition} />}
        </div>
      </div>
      
      <button 
        className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded transition-colors text-white font-medium mt-2"
        onClick={onClearGrid}
      >
        Clear Grid
      </button>
    </div>
  );
};

export default Grid;